
import { useState, useEffect } from 'react';

const AccessibilityButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState('normal');
  const [highContrast, setHighContrast] = useState(false);

  // Load saved settings on component mount
  useEffect(() => {
    const savedFontSize = localStorage.getItem('accessibility-font-size');
    const savedContrast = localStorage.getItem('accessibility-high-contrast');
    
    if (savedFontSize) {
      setFontSize(savedFontSize);
      changeFontSize(savedFontSize);
    }
    
    if (savedContrast === 'true') {
      setHighContrast(true);
      document.body.classList.add('high-contrast');
    }
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const changeFontSize = (size: string) => {
    setFontSize(size);
    const root = document.documentElement;
    
    switch (size) {
      case 'small':
        root.style.fontSize = '14px';
        break;
      case 'large':
        root.style.fontSize = '18px';
        break;
      case 'xlarge':
        root.style.fontSize = '22px';
        break;
      default:
        root.style.fontSize = '16px';
    }
    
    localStorage.setItem('accessibility-font-size', size);
  };

  const toggleHighContrast = () => {
    const newContrast = !highContrast;
    setHighContrast(newContrast);
    
    if (newContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
    
    localStorage.setItem('accessibility-high-contrast', newContrast.toString());
  };

  const resetSettings = () => {
    setFontSize('normal');
    setHighContrast(false);
    document.documentElement.style.fontSize = '16px';
    document.body.classList.remove('high-contrast');
    localStorage.removeItem('accessibility-font-size');
    localStorage.removeItem('accessibility-high-contrast');
  };

  return (
    <>
      <button
        onClick={toggleMenu}
        className="fixed bottom-4 right-4 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-40"
        title="Barrierefreiheit-Einstellungen"
        aria-label="Barrierefreiheit-Einstellungen öffnen"
      >
        <i className="ri-wheelchair-line text-lg"></i>
      </button>

      {isOpen && (
        <div className="fixed bottom-20 right-4 bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-50 w-80">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Barrierefreiheit</h3>
            <button
              onClick={toggleMenu}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Menü schließen"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schriftgröße
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => changeFontSize('small')}
                  className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                    fontSize === 'small'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Klein
                </button>
                <button
                  onClick={() => changeFontSize('normal')}
                  className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                    fontSize === 'normal'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Normal
                </button>
                <button
                  onClick={() => changeFontSize('large')}
                  className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                    fontSize === 'large'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Groß
                </button>
                <button
                  onClick={() => changeFontSize('xlarge')}
                  className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                    fontSize === 'xlarge'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Sehr groß
                </button>
              </div>
            </div>

            <div>
              <label className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Hoher Kontrast</span>
                <div className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={highContrast}
                    onChange={toggleHighContrast}
                    className="sr-only"
                  />
                  <div className={`w-11 h-6 rounded-full transition-colors ${
                    highContrast ? 'bg-blue-600' : 'bg-gray-300'
                  }`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                      highContrast ? 'translate-x-5' : 'translate-x-0'
                    } mt-0.5 ml-0.5`}></div>
                  </div>
                </div>
              </label>
            </div>

            <div className="pt-3 border-t border-gray-200">
              <button
                onClick={resetSettings}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
              >
                Einstellungen zurücksetzen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityButton;
